<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: 'dd.mm.yyyy --:--'
  }
})

const emit = defineEmits(['update:modelValue'])

const showPicker = ref(false)
const currentDate = ref(new Date())
const selectedDate = ref('')
const selectedHour = ref('00')
const selectedMinute = ref('00')
const pickerRef = ref(null)
const dropdownPosition = ref({ left: 0, top: 0 })
const hoursListRef = ref(null)
const minutesListRef = ref(null)

const months = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
]

const currentMonth = computed(() => {
  return `${months[currentDate.value.getMonth()]} ${currentDate.value.getFullYear()}`
})

const daysInMonth = computed(() => {
  const year = currentDate.value.getFullYear()
  const month = currentDate.value.getMonth()
  const firstDay = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  
  const days = []
  const previousMonthDays = new Date(year, month, 0).getDate()
  
  // Previous month days - adjust for Monday as first day of week
  const daysToShow = firstDay === 0 ? 6 : firstDay - 1
  for (let i = daysToShow; i > 0; i--) {
    days.push({
      day: previousMonthDays - i + 1,
      isCurrentMonth: false,
      isToday: false
    })
  }
  
  // Current month days
  const today = new Date()
  for (let i = 1; i <= daysInMonth; i++) {
    days.push({
      day: i,
      isCurrentMonth: true,
      isToday: today.getDate() === i && 
               today.getMonth() === currentDate.value.getMonth() &&
               today.getFullYear() === currentDate.value.getFullYear()
    })
  }
  
  // Next month days to complete the last row
  const totalDaysShown = days.length
  const remainingDays = 42 - totalDaysShown // 6 rows × 7 days = 42
  
  for (let i = 1; i <= remainingDays; i++) {
    days.push({
      day: i,
      isCurrentMonth: false,
      isToday: false
    })
  }
  
  return days
})

const previousMonth = () => {
  currentDate.value = new Date(currentDate.value.getFullYear(), currentDate.value.getMonth() - 1)
}

const nextMonth = () => {
  currentDate.value = new Date(currentDate.value.getFullYear(), currentDate.value.getMonth() + 1)
}

const selectDate = (day) => {
  // Allow selection of any date (current month, previous month, or next month)
  let year, month, dayNum;
  
  if (day.isCurrentMonth) {
    year = currentDate.value.getFullYear();
    month = (currentDate.value.getMonth() + 1).toString().padStart(2, '0');
    dayNum = day.day.toString().padStart(2, '0');
  } else {
    // Determine if it's a previous month or next month date
    const currentMonthDays = new Date(currentDate.value.getFullYear(), currentDate.value.getMonth() + 1, 0).getDate();
    const firstDayOfMonth = new Date(currentDate.value.getFullYear(), currentDate.value.getMonth(), 1).getDay();
    const daysFromPrevMonth = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;
    
    // Calculate total days shown before current month
    const totalDaysBeforeCurrent = daysFromPrevMonth;
    
    // Find the position of this day in the calendar grid
    const dayIndex = daysInMonth.value.findIndex(d => d.day === day.day && !d.isCurrentMonth);
    
    if (dayIndex < totalDaysBeforeCurrent) {
      // This is a previous month date
      const prevMonth = currentDate.value.getMonth() === 0 ? 11 : currentDate.value.getMonth() - 1;
      const prevYear = currentDate.value.getMonth() === 0 ? currentDate.value.getFullYear() - 1 : currentDate.value.getFullYear();
      year = prevYear;
      month = (prevMonth + 1).toString().padStart(2, '0');
      dayNum = day.day.toString().padStart(2, '0');
    } else {
      // This is a next month date
      const nextMonth = currentDate.value.getMonth() === 11 ? 0 : currentDate.value.getMonth() + 1;
      const nextYear = currentDate.value.getMonth() === 11 ? currentDate.value.getFullYear() + 1 : currentDate.value.getFullYear();
      year = nextYear;
      month = (nextMonth + 1).toString().padStart(2, '0');
      dayNum = day.day.toString().padStart(2, '0');
    }
  }
  
  selectedDate.value = `${year}-${month}-${dayNum}`;
  updateDateTime()
}

const updateDateTime = () => {
  if (!selectedDate.value) return
  const formattedDate = selectedDate.value.split('-').reverse().join('.')
  const formattedTime = `${selectedHour.value}:${selectedMinute.value}`
  emit('update:modelValue', `${formattedDate}, ${formattedTime}`)
}

const goToToday = () => {
  currentDate.value = new Date()
}

// Close picker when clicking outside
const handleClickOutside = (event) => {
  const picker = event.target.closest('.datetime-picker')
  const dropdown = event.target.closest('.datetime-dropdown')
  if (!picker && !dropdown) {
    showPicker.value = false
  }
}

// Calculate dropdown position
const updateDropdownPosition = () => {
  nextTick(() => {
    if (pickerRef.value) {
      const rect = pickerRef.value.getBoundingClientRect()
      dropdownPosition.value = {
        left: rect.left,
        top: rect.bottom + 12
      }
    }
  })
}

// Scroll to selected time in the list
const scrollToSelectedTime = () => {
  // Use setTimeout to ensure DOM is fully rendered
  setTimeout(() => {
    // Scroll hours list
    if (hoursListRef.value) {
      const hourIndex = parseInt(selectedHour.value)
      const hourElement = hoursListRef.value.children[hourIndex]
      if (hourElement) {
        hourElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
    
    // Scroll minutes list
    if (minutesListRef.value) {
      const minuteIndex = parseInt(selectedMinute.value)
      const minuteElement = minutesListRef.value.children[minuteIndex]
      if (minuteElement) {
        minuteElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
  }, 100)
}

// Parse modelValue to initialize picker state
const parseModelValue = () => {
  if (!props.modelValue) {
    // If no value, use current date/time
    const now = new Date()
    selectedDate.value = `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}-${now.getDate().toString().padStart(2, '0')}`
    selectedHour.value = now.getHours().toString().padStart(2, '0')
    selectedMinute.value = now.getMinutes().toString().padStart(2, '0')
    currentDate.value = new Date(now.getFullYear(), now.getMonth(), 1)
    scrollToSelectedTime()
    return
  }

  // Parse format: "dd.mm.yyyy, HH:mm"
  const parts = props.modelValue.split(', ')
  if (parts.length === 2) {
    const datePart = parts[0] // "dd.mm.yyyy"
    const timePart = parts[1] // "HH:mm"
    
    // Parse date
    const [day, month, year] = datePart.split('.')
    if (day && month && year) {
      selectedDate.value = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
      // Set currentDate to show the correct month
      currentDate.value = new Date(parseInt(year), parseInt(month) - 1, 1)
    }
    
    // Parse time
    const [hour, minute] = timePart.split(':')
    if (hour !== undefined) {
      selectedHour.value = hour.padStart(2, '0')
    }
    if (minute !== undefined) {
      selectedMinute.value = minute.padStart(2, '0')
    }
    
    scrollToSelectedTime()
  }
}

// Watch for showPicker changes to update position and initialize values
watch(showPicker, (newVal) => {
  if (newVal) {
    parseModelValue()
    updateDropdownPosition()
  }
})

// Also watch modelValue changes to sync when value is updated externally
watch(() => props.modelValue, () => {
  if (showPicker.value) {
    parseModelValue()
  }
})

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  window.addEventListener('scroll', updateDropdownPosition, true)
  window.addEventListener('resize', updateDropdownPosition)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  window.removeEventListener('scroll', updateDropdownPosition, true)
  window.removeEventListener('resize', updateDropdownPosition)
})

const isToday = (day) => {
  const today = new Date();
  return (
    day.isCurrentMonth &&
    today.getDate() === day.day &&
    today.getMonth() === currentDate.value.getMonth() &&
    today.getFullYear() === currentDate.value.getFullYear()
  );
};

const isSelected = (day) => {
  if (!selectedDate.value) return false;
  const selected = new Date(selectedDate.value);
  
  if (day.isCurrentMonth) {
    return (
      selected.getDate() === day.day &&
      selected.getMonth() === currentDate.value.getMonth() &&
      selected.getFullYear() === currentDate.value.getFullYear()
    );
  } else {
    // Determine if it's a previous month or next month date
    const currentMonthDays = new Date(currentDate.value.getFullYear(), currentDate.value.getMonth() + 1, 0).getDate();
    const firstDayOfMonth = new Date(currentDate.value.getFullYear(), currentDate.value.getMonth(), 1).getDay();
    const daysFromPrevMonth = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;
    
    // Calculate total days shown before current month
    const totalDaysBeforeCurrent = daysFromPrevMonth;
    
    // Find the position of this day in the calendar grid
    const dayIndex = daysInMonth.value.findIndex(d => d.day === day.day && !d.isCurrentMonth);
    
    if (dayIndex < totalDaysBeforeCurrent) {
      // This is a previous month date
      const prevMonth = currentDate.value.getMonth() === 0 ? 11 : currentDate.value.getMonth() - 1;
      const prevYear = currentDate.value.getMonth() === 0 ? currentDate.value.getFullYear() - 1 : currentDate.value.getFullYear();
      return (
        selected.getDate() === day.day &&
        selected.getMonth() === prevMonth &&
        selected.getFullYear() === prevYear
      );
    } else {
      // This is a next month date
      const nextMonth = currentDate.value.getMonth() === 11 ? 0 : currentDate.value.getMonth() + 1;
      const nextYear = currentDate.value.getMonth() === 11 ? currentDate.value.getFullYear() + 1 : currentDate.value.getFullYear();
      return (
        selected.getDate() === day.day &&
        selected.getMonth() === nextMonth &&
        selected.getFullYear() === nextYear
      );
    }
  }
};
</script>

<template>
  <div class="relative datetime-picker" ref="pickerRef">
    <input 
      type="text"
      :value="modelValue"
      :placeholder="placeholder"
      class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-pink-500"
      @focus="showPicker = true; updateDropdownPosition()"
      readonly
    >
    <button class="absolute right-2 top-2 text-gray-400">
      <i class="far fa-calendar"></i>
    </button>

    <!-- Calendar Dropdown -->
    <Teleport to="body">
      <div v-if="showPicker" class="datetime-dropdown fixed bg-white rounded-lg shadow-lg z-[100] w-[600px]" :style="{ left: dropdownPosition.left + 'px', top: dropdownPosition.top + 'px' }">
      <div class="flex p-4 justify-between items-center border-b">
        <button @click="previousMonth" class="text-gray-500 hover:text-gray-700">
          <i class="fas fa-chevron-left"></i>
        </button>
        <span class="font-medium">{{ currentMonth }}</span>
        <button @click="nextMonth" class="text-gray-500 hover:text-gray-700">
          <i class="fas fa-chevron-right"></i>
        </button>
      </div>

      <div class="grid grid-cols-2 gap-4 p-4">
        <!-- Calendar -->
        <div>
          <div class="grid grid-cols-7 gap-1 mb-2">
            <div v-for="day in ['M', 'T', 'W', 'T', 'F', 'S', 'S']" 
                 :key="day" 
                 class="text-center text-sm text-gray-500">
              {{ day }}
            </div>
          </div>
          <div class="grid grid-cols-7 gap-1">
            <button 
              v-for="(day, index) in daysInMonth" 
              :key="index"
              @click="selectDate(day)"
              class="date-btn"
              :class="{
                'inactive': !day.isCurrentMonth,
                'selected': isSelected(day),
                'today': isToday(day) && !isSelected(day),
                'active': day.isCurrentMonth
              }"
            >
              {{ day.day }}
            </button>
          </div>
          <div class="mt-4 flex justify-between">
            <button 
              @click="goToToday"
              class="text-pink-500 hover:text-pink-600"
            >
              Today
            </button>
            <button 
              @click="showPicker = false"
              class="text-gray-500 hover:text-gray-600"
            >
              Close
            </button>
          </div>
        </div>

        <!-- Time Selection -->
        <div class="border-l pl-4">
          <div class="grid grid-cols-2 gap-4">
            <div>
              <div class="text-center mb-2 text-gray-500">Hours</div>
              <div ref="hoursListRef" class="h-[200px] overflow-y-auto">
                <button 
                  v-for="hour in 24" 
                  :key="hour-1"
                  @click="selectedHour = (hour-1).toString().padStart(2, '0'); updateDateTime()"
                  class="block w-full py-1 text-center hover:bg-pink-50 transition-colors"
                  :class="{'bg-pink-500 text-white': selectedHour === (hour-1).toString().padStart(2, '0')}"
                >
                  {{ (hour-1).toString().padStart(2, '0') }}
                </button>
              </div>
            </div>
            <div>
              <div class="text-center mb-2 text-gray-500">Minutes</div>
              <div ref="minutesListRef" class="h-[200px] overflow-y-auto">
                <button 
                  v-for="minute in 60" 
                  :key="minute-1"
                  @click="selectedMinute = (minute-1).toString().padStart(2, '0'); updateDateTime()"
                  class="block w-full py-1 text-center hover:bg-pink-50 transition-colors"
                  :class="{'bg-pink-500 text-white': selectedMinute === (minute-1).toString().padStart(2, '0')}"
                >
                  {{ (minute-1).toString().padStart(2, '0') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
/* Custom scrollbar for time selection */
.overflow-y-auto {
  scrollbar-width: thin;
  scrollbar-color: #e5e7eb transparent;
}

.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: #e5e7eb;
  border-radius: 3px;
}

.date-btn.selected {
  background: #ec4899;
  color: #fff;
  box-shadow: 0 0 0 2px #fff, 0 0 0 5px #ec4899;
  z-index: 2;
  position: relative;
  font-size: 1rem;
  width: 36px;
  height: 36px;
}
.date-btn.selected:hover {
  box-shadow: 0 0 0 2px #fff, 0 0 0 7px #ec4899;
  color: #ec4899;
  background: #fff;
}

.date-btn.today {
  border: 1.5px solid #ec4899;
  color: #ec4899;
  background: #fff;
  font-size: 0.95rem;
}

.date-btn.active:not(.selected):hover {
  background: #fce7f3;
  color: #ec4899;
}

.date-btn.inactive {
  color: #9ca3af;
  background: #f9fafb;
  font-weight: normal;
}

.date-btn {
  @apply flex items-center justify-center;
  width: 40px;
  height: 40px;
  padding: 0;
  margin: 2px;
  font-size: 1rem;
  border-radius: 8px;
  border: none;
  background: #fff;
  font-weight: bold;
  transition: box-shadow 0.15s, background 0.15s, color 0.15s, font-size 0.15s;
}
</style> 